/**
 * All API calls related to user setup & data fetching during navigation.
 * Keeping this separate means guards stay readable and this logic is testable in isolation.
 */

import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import store from '@/store';

const BASE_URL = process.env.VUE_APP_CBE_BASE_URL;

const authHeaders = token => ({ Authorization: `Bearer ${token}` });

// ── API calls ────────────────────────────────────────────────────────────────

async function getOrCreatePiccoloUser(auth0User, token) {
  try {
    const { data } = await axios.get(
      `${BASE_URL}/piccolo/users/${auth0User.sub}`,
      { headers: authHeaders(token) }
    );
    return data;
  } catch (err) {
    if (err.response?.status !== 404) throw err;

    const { data } = await axios.post(
      `${BASE_URL}/piccolo/users`,
      {
        auth0_id: auth0User.sub,
        email: auth0User.email,
        organization: '',
        terms_version: Number(process.env.VUE_APP_TERMS_VERSION),
      },
      { headers: authHeaders(token) }
    );
    return data;
  }
}

async function fetchBillingInfo(userId, token) {
  // Return cached value if already known
  const cached = store.state.authentication.hasBillingInfo;
  if (cached !== null) return cached;

  try {
    const { status } = await axios.get(
      `${BASE_URL}/piccolo/billing/address/${userId}`,
      { headers: authHeaders(token) }
    );
    return status === 200;
  } catch {
    return false;
  }
}

async function fetchSpecialFileAccess(userId, token) {
  try {
    const { data } = await axios.get(
      `${BASE_URL}/piccolo/specialfiles/${userId}`,
      { headers: authHeaders(token) }
    );
    return data.length > 0;
  } catch {
    return false;
  }
}

async function fetchQuoteStatus(userId, token) {
  try {
    const { data } = await axios.get(
      `${BASE_URL}/piccolo/billing/quote-status/${userId}`,
      { headers: authHeaders(token) }
    );
    return data.has_quote;
  } catch (err) {
    console.error('Error fetching quote status:', err);
    return false;
  }
}

// ── Public entry point ───────────────────────────────────────────────────────

export async function loadUserData(auth0User, getAccessTokenSilently) {
  const token = await getAccessTokenSilently();
  const decoded = jwtDecode(token);

  const [piccoloUser, hasBillingInfo, hasSpecialFileAccess, hasQuote] = await Promise.all([
    getOrCreatePiccoloUser(auth0User, token),
    fetchBillingInfo(auth0User.sub, token),
    fetchSpecialFileAccess(auth0User.sub, token),
    fetchQuoteStatus(auth0User.sub, token),
  ]);

  const permissions =
    decoded['https://structure.io/permissions'] ??
    decoded.permissions ??
    [];
  const roles =
    decoded['https://structure.io/roles'] ??
    auth0User['https://structure.io/roles'] ??
    [];

  const ORG_ROLE_NAMES = ['org-owner', 'org-admin', 'org-developer', 'org-accountant', 'org-marketer'];

  // org_id may be on the access token, the ID token, or both depending on when tokens were issued
  const orgId =
    decoded['https://structure.io/org_id'] ??
    auth0User['https://structure.io/org_id'] ??
    null;

  const orgRoles = (roles || []).filter(r => ORG_ROLE_NAMES.includes(r));
  const isOrgMember = !!orgId || orgRoles.length > 0;
  store.commit('SET_ORG', { isOrgMember, orgId, orgRoles });

  if (isOrgMember) {
    const cached = store.getters.orgBranding;
    if (!cached?.fetched) {
      const requestOrgId = orgId;
      axios.get(
        `${BASE_URL}/piccolo/organizations/me`,
        { headers: authHeaders(token) }
      ).then(({ data: orgData }) => {
        // Discard if the user signed out or a different org is now active.
        if (store.getters.orgId !== requestOrgId) return;
        store.commit('SET_ORG_BRANDING', {
          logoUrl: orgData.logo_url || null,
          primaryColor: orgData.primary_color || null,
          pageBackgroundColor: orgData.page_background_color || null,
        });
      }).catch(() => {
        // Mark fetched so failed attempts don't retry on every navigation.
        if (store.getters.orgId === requestOrgId) {
          store.commit('SET_ORG_BRANDING', { logoUrl: null, primaryColor: null, pageBackgroundColor: null });
        }
      });
    }
  }

  store.commit('SET_HAS_SPECIAL_FILE_ACCESS', hasSpecialFileAccess);
  store.commit('SET_AUTHENTICATION', {
    userIsAuthorized: true,
    roles,
    permissions,
    termsVersion: piccoloUser.terms_version,
    is_admin: piccoloUser.is_admin,
    account_status: piccoloUser.account_status,
    tier: piccoloUser.tier,
    customer_type: piccoloUser.customer_type ?? null,
    hasBillingInfo,
    hasQuote,
    is_trial: piccoloUser.is_trial,
  });

  return {
    ...piccoloUser,
    has_billing_info: hasBillingInfo,
    has_quote: hasQuote,
    is_org_member: isOrgMember,
  };
}